// Timeline utility functions

/**
 * Auto-scroll logic for timeline elements
 * @param {MouseEvent} e - The mouse event
 * @param {HTMLElement} scrollContainer - The container that scrolls
 * @param {Object} options - Configuration options
 * @returns {Object} - New scroll position and whether scrolling occurred
 */
export const autoScrollTimeline = (e, scrollContainer, options = {}) => {
  const {
    scrollThreshold = 10,  // Pixels from edge to start scrolling
    scrollSpeed = 10,      // Pixels to scroll per frame
  } = options;

  if (!scrollContainer) return { scrolled: false };

  const containerRect = scrollContainer.getBoundingClientRect();
  const relativeX = e.clientX - containerRect.left;

  let newScrollLeft = scrollContainer.scrollLeft;
  let scrolled = false;

  // Check if we need to scroll right
  if (relativeX > containerRect.width - scrollThreshold) {
    const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
    newScrollLeft = Math.min(scrollContainer.scrollLeft + scrollSpeed, maxScroll);
    scrolled = true;
  }
  // Check if we need to scroll left
  else if (relativeX < scrollThreshold) {
    newScrollLeft = Math.max(scrollContainer.scrollLeft - scrollSpeed, 0);
    scrolled = true;
  }

  if (scrolled) {
    scrollContainer.scrollLeft = newScrollLeft;
  }

  return {
    scrolled,
    newScrollLeft,
    relativeX
  };
};

/**
 * Auto-scroll logic for timeline elements (vertical)
 * @param {MouseEvent} e - The mouse event
 * @param {HTMLElement} scrollContainer - The container that scrolls
 * @param {Object} options - Configuration options
 * @returns {Object} - New scroll position and whether scrolling occurred
 */
export const autoScrollTimelineVertical = (e, scrollContainer, options = {}) => {
  const {
    scrollThreshold = 10,  // Pixels from edge to start scrolling
    scrollSpeed = 10,      // Pixels to scroll per frame
  } = options;

  if (!scrollContainer) return { scrolled: false };

  const containerRect = scrollContainer.getBoundingClientRect();
  const relativeY = e.clientY - containerRect.top;

  let newScrollTop = scrollContainer.scrollTop;
  let scrolled = false;

  // Check if we need to scroll down
  if (relativeY > containerRect.height - scrollThreshold) {
    const maxScroll = scrollContainer.scrollHeight - scrollContainer.clientHeight;
    newScrollTop = Math.min(scrollContainer.scrollTop + scrollSpeed, maxScroll);
    scrolled = true;
  }
  // Check if we need to scroll up
  else if (relativeY < scrollThreshold) {
    newScrollTop = Math.max(scrollContainer.scrollTop - scrollSpeed, 0);
    scrolled = true;
  }

  if (scrolled) {
    scrollContainer.scrollTop = newScrollTop;
  }

  return {
    scrolled,
    newScrollTop,
    relativeY
  };
};

/**
 * Calculate timeline position from mouse event
 * @param {MouseEvent} e - The mouse event
 * @param {HTMLElement} timelineRef - The timeline reference element
 * @param {number} scrollLeft - Current scroll position
 * @param {number} timelineWidth - Total timeline width
 * @param {number} duration - Timeline duration
 * @returns {Object} - Calculated time and percentage
 */
export const calculateTimelinePosition = (e, timelineRef, scrollLeft, timelineWidth, duration) => {
  if (!timelineRef) return { time: 0, percentage: 0 };

  const rect = timelineRef.getBoundingClientRect();
  const x = e.clientX - rect.left + scrollLeft;
  const percentage = Math.max(0, Math.min(100, (x / timelineWidth) * 100));
  const time = (percentage / 100) * duration;

  return { time, percentage, x };
};